/**
 * Build BioSDK inner JSON with dynamic sample.segments[] and gallery[].
 */

export const toSignedByteJson = (arrayBufferOrUint8) => {
  const unsigned =
    arrayBufferOrUint8 instanceof ArrayBuffer
      ? new Uint8Array(arrayBufferOrUint8)
      : arrayBufferOrUint8 instanceof Uint8Array
        ? arrayBufferOrUint8
        : new Uint8Array(arrayBufferOrUint8);
  const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
  return JSON.stringify(signed);
};

const FACE_SEGMENT_TEMPLATE = {
  version: { major: 1, minor: 1 },
  cbeffversion: { major: 1, minor: 1 },
  birInfo: { integrity: false },
  bdbInfo: {
    format: { organization: 'Mosip', type: '8' },
    creationDate: {
      date: { year: 2022, month: 11, day: 10 },
      time: { hour: 18, minute: 11, second: 55, nano: 338000000 },
    },
    type: ['FACE'],
    subtype: ['UNKNOWN'],
    level: 'RAW',
    purpose: 'ENROLL',
    quality: { algorithm: { organization: 'HMAC', type: 'SHA-256' }, score: 100 },
  },
};

const IRIS_SEGMENT_TEMPLATE = {
  version: { major: 1, minor: 1 },
  cbeffversion: { major: 1, minor: 1 },
  birInfo: { integrity: false },
  bdbInfo: {
    format: { organization: 'Mosip', type: '9' },
    creationDate: {
      date: { year: 2024, month: 1, day: 1 },
      time: { hour: 12, minute: 0, second: 0, nano: 0 },
    },
    type: ['IRIS'],
    subtype: ['Left'],
    level: 'RAW',
    purpose: 'ENROLL',
    quality: { algorithm: { organization: 'HMAC', type: 'SHA-256' }, score: 100 },
  },
};

export const buildFaceRawSegment = (bdbBytes, purpose = 'ENROLL') => ({
  ...FACE_SEGMENT_TEMPLATE,
  bdbInfo: {
    ...FACE_SEGMENT_TEMPLATE.bdbInfo,
    purpose,
    level: 'RAW',
  },
  bdb: JSON.parse(toSignedByteJson(bdbBytes)),
});

export const buildFaceProcessedSegment = (bdbBytes, purpose = 'VERIFY') => ({
  ...FACE_SEGMENT_TEMPLATE,
  bdbInfo: {
    ...FACE_SEGMENT_TEMPLATE.bdbInfo,
    purpose,
    level: 'PROCESSED',
  },
  bdb: JSON.parse(toSignedByteJson(bdbBytes)),
});

export const buildIrisRawSegment = (bdbBytes, subtype = 'Left', purpose = 'ENROLL') => ({
  ...IRIS_SEGMENT_TEMPLATE,
  bdbInfo: {
    ...IRIS_SEGMENT_TEMPLATE.bdbInfo,
    subtype: [subtype],
    purpose,
    level: 'RAW',
  },
  bdb: JSON.parse(toSignedByteJson(bdbBytes)),
});

export const buildFaceExtractInnerRequest = ({ segments, flags = {} }) => ({
  sample: { segments, others: {} },
  modalitiesToExtract: ['FACE'],
  flags: { 'io.ooru.sdk.input.type': 'raw', ...flags },
});

export const buildIrisExtractInnerRequest = ({ segments, flags = {} }) => ({
  sample: { segments, others: {} },
  modalitiesToExtract: ['IRIS'],
  flags: {
    'iris.preprocessing.enabled': 'true',
    'iris.normalization.enabled': 'true',
    'io.ooru.sdk.input.type': 'raw',
    ...flags,
  },
});

export const buildIrisProcessedSegment = (bdbBytes, subtype = 'Left', purpose = 'VERIFY') => ({
  ...IRIS_SEGMENT_TEMPLATE,
  bdbInfo: {
    ...IRIS_SEGMENT_TEMPLATE.bdbInfo,
    subtype: [subtype],
    purpose,
    level: 'PROCESSED',
  },
  bdb: JSON.parse(toSignedByteJson(bdbBytes)),
});

export const buildIrisMatchInnerRequest = ({
  probeSegment,
  gallerySegments,
  probeLevel = 'PROCESSED',
  galleryLevel = 'PROCESSED',
}) => {
  const probe = {
    ...probeSegment,
    bdbInfo: { ...probeSegment.bdbInfo, level: probeLevel, purpose: 'VERIFY' },
  };
  const gallery = gallerySegments.map((seg) => ({
    segments: [
      {
        ...seg,
        bdbInfo: { ...seg.bdbInfo, level: galleryLevel, purpose: 'VERIFY' },
      },
    ],
    others: {},
  }));
  return {
    sample: { segments: [probe], others: {} },
    gallery,
    modalitiesToMatch: ['IRIS'],
  };
};

export const buildFaceMatchInnerRequest = ({
  probeSegment,
  gallerySegments,
  probeLevel = 'PROCESSED',
  galleryLevel = 'PROCESSED',
}) => {
  const probe = {
    ...probeSegment,
    bdbInfo: { ...probeSegment.bdbInfo, level: probeLevel, purpose: 'VERIFY' },
  };
  const gallery = gallerySegments.map((seg) => ({
    segments: [
      {
        ...seg,
        bdbInfo: { ...seg.bdbInfo, level: galleryLevel, purpose: 'VERIFY' },
      },
    ],
    others: {},
  }));
  return {
    sample: { segments: [probe], others: {} },
    gallery,
    modalitiesToMatch: ['FACE'],
  };
};

export const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const wrapEncodedRequest = async (innerObject) => {
  const versionResponse = await fetch('/extract_request-template.json');
  if (!versionResponse.ok) throw new Error('Version request template fetch failed');
  const versionText = await versionResponse.text();
  const innerJson = JSON.stringify(innerObject);
  const encoded = arrayBufferToBase64(new TextEncoder().encode(innerJson));
  return versionText.replace('encoded_request', encoded);
};

export const wrapMatchRequest = async (innerObject) => {
  const versionResponse = await fetch('/match_request-template.json');
  if (!versionResponse.ok) throw new Error('Match request template fetch failed');
  const versionText = await versionResponse.text();
  const innerJson = JSON.stringify(innerObject);
  const encoded = btoa(unescape(encodeURIComponent(innerJson)));
  const body = JSON.parse(versionText.replace('encoded_request', encoded));
  body.operation = 'match';
  return body;
};

export async function fileToRawBdbBytes(file) {
  return new Uint8Array(await file.arrayBuffer());
}
